import { useState } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { Box, Grid, Button, TextField, IconButton, InputAdornment } from '@mui/material'
import { RemoveRedEyeOutlined as IRemoveRedEye, VisibilityOffOutlined as IVisibilityOff } from '@mui/icons-material'
import { env } from '../../core/Env'
import { rqx } from '../../core/request/API'
import { continueAfterAuth } from '../pwaShell'
import { pslstyle } from '../../core/styling/si'
import { validateEmail } from '../../core/validator/Validator'
import { SnakeLoader } from '../../core/loader/SnakeLoader'
import { grantToolAccess } from './AddAccess'
import { goLogin } from './goLogin'
import LPFSS from '../../assets/images/logos/pofsis_no_border.png'
import GSPFSS from '../../assets/images/logos/pofsis_grayscale.jpg'
import LPSL from '../../assets/images/logos/psl_logo.png'
import LBIZ from '../../assets/images/logos/biz_logo.png'

const parseLogo = (logo) => {
    if (!logo) return null
    try {
        return typeof logo === 'string' ? JSON.parse(logo) : logo
    } catch (err) {
        return null
    }
}

/**
 * Biz mobile EntryAccess: account exists but no email on record.
 * Step 1: password on mobile. Step 2: link business email (skipped if ainfo.email).
 * Then grant (if no tool access) or goLogin with email.
 */
export const LinkEmailAccess = (props) => (
    <Router>
        <LinkEmailAccessForm {...props} />
    </Router>
)

const LinkEmailAccessForm = ({
    account,
    mobile,
    platform,
    apiPlatform,
    platformQuery,
    tool,
    authCallback,
    from,
    subcb,
    pricing,
    ref_a_frnd,
    toolAccess,
}) => {
    const attemptPlatform = apiPlatform || platform || 'biz'
    const mobileNum = mobile || account
    const classes = pslstyle(attemptPlatform)
    const toolName = tool?.name || 'this application'
    const toolLogo = parseLogo(tool?.logo) || LBIZ

    const [step, setStep] = useState('password') // password | email
    const [password, setPassword] = useState('')
    const [showPass, setShowPass] = useState(false)
    const [bizEmail, setBizEmail] = useState('')
    const [ainfo, setAinfo] = useState(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const persistSession = () => {
        const tinfo = tool
            ? [{ name: tool.name, tid: tool.tid, type: tool.type, logo: tool.logo }]
            : [{ name: 'Tool', tid: null, type: 1, logo: null }]
        const strg = {
            cb: authCallback,
            tid: tool?.tid ?? null,
            tinfo,
            expiration: '',
            platform: platformQuery || 'business',
            tsa_key: from,
            pricing,
            subcb,
            ref_a_frnd,
        }
        localStorage.setItem('pm_tool_session', JSON.stringify(strg))
        return strg
    }

    const finishWithAinfo = async (finalAinfo, linkedEmail) => {
        if (toolAccess === 'no') {
            const grant = await grantToolAccess({
                ainfo: finalAinfo,
                platform: attemptPlatform,
                tid: tool?.tid,
            })
            if (grant?.msg !== 'success') {
                setError(grant?.msg || 'Something went wrong. Please try again.')
                setBusy(false)
                return
            }
            persistSession()
            const params = new URLSearchParams()
            params.set('ainfo', JSON.stringify(finalAinfo))
            if (subcb) params.set('subcb', subcb)
            params.set('ref', 'auth')
            continueAfterAuth(`${authCallback}?${params.toString()}`, { platform: attemptPlatform })
            return
        }

        persistSession()
        const loginErr = goLogin({
            account: linkedEmail || finalAinfo?.email,
            va: 'email',
            platform: attemptPlatform,
            apiPlatform: attemptPlatform,
            platformQuery: platformQuery || 'business',
            tool,
            authCallback,
            from,
            pricing,
            subcb,
            ref_a_frnd,
        })
        if (loginErr) {
            setError(loginErr)
            setBusy(false)
        }
    }

    const submitPassword = async () => {
        if (busy) return
        setError('')
        if (!password) {
            setError('Password is required')
            return
        }
        if (!authCallback) {
            setError('Callback URL is missing')
            return
        }

        setBusy(true)
        try {
            const pass = password.includes('#') ? encodeURIComponent(password) : password
            const attempt = await rqx.get(
                `<<tool>>/${env()}/${attemptPlatform}/si/attempt/mobile_num`,
                { account: mobileNum, password: pass, ref: 2, platform: attemptPlatform }
            )

            if (attempt?.msg !== 'valid' || !attempt.ainfo) {
                setError(attempt?.msg === 'valid' ? 'Missing account info' : 'Incorrect password.')
                setBusy(false)
                return
            }

            setAinfo(attempt.ainfo)

            if (attempt.ainfo.email) {
                await finishWithAinfo(attempt.ainfo, attempt.ainfo.email)
                return
            }

            setStep('email')
            setBusy(false)
        } catch (err) {
            setError(`request failed: ${err?.message || err}`)
            setBusy(false)
        }
    }

    const submitEmail = async () => {
        if (busy) return
        setError('')
        if (!bizEmail || !validateEmail(bizEmail)) {
            setError('A valid business email is required')
            return
        }
        if (!ainfo?.aid) {
            setError('Missing account info')
            return
        }

        setBusy(true)
        try {
            const email = bizEmail.trim()

            // Existing API: SI email ref 1 — msg "valid" means account already exists
            const check = await rqx.get(
                `<<tool>>/${env()}/${attemptPlatform}/si/attempt/email`,
                { account: email, ref: 1, platform: attemptPlatform }
            )

            if (check?.msg === 'valid') {
                setError('This email is already registered to another account.')
                setBusy(false)
                return
            }

            const body = {
                email,
                aid: ainfo.aid,
                account_type: 1,
            }
            const uacc = await rqx.post(
                `<<global>>/${env() === 'local' ? 'dev' : env()}/account/updateaccount`,
                body
            )

            if (uacc?.msg !== 'success') {
                setError(uacc?.msg || 'Unable to link email. Please try again.')
                setBusy(false)
                return
            }

            const finalAinfo = uacc?.data
                ? uacc.data
                : { ...ainfo, email }

            await finishWithAinfo(finalAinfo, finalAinfo.email || email)
        } catch (err) {
            setError(`request failed: ${err?.message || err}`)
            setBusy(false)
        }
    }

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" height="100%" minHeight="100vh">
            <Box borderRadius={5} p={3}>
                <Box
                    boxShadow="0px 0px 4px rgba(0, 0, 0, 0.04), 0px 8px 16px rgba(0, 0, 0, 0.08)"
                    bgcolor="#FFF"
                    borderRadius="8px"
                    px={4}
                    py={5}
                    width="auto"
                    height="auto"
                    minWidth="480px"
                >
                    <Grid container spacing={3}>
                        <Grid container flexDirection="column" item xs={12}>
                            <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                                <Box p={1} display="flex" alignItems="center">
                                    <img src={LPFSS} alt="POFSIS Logo" width="52" />
                                </Box>
                                <Box className="personal-logo-px-1" ml={3} sx={{ backgroundImage: `url(${toolLogo || LPSL})` }} />
                            </Box>

                            <Box mt={3} fontSize="30px" fontWeight="600" align="center" color="#101828">
                                {step === 'password' ? 'Verify it’s you' : 'Link your business email'}
                            </Box>
                            <Box mt={1.5} fontSize="16px" fontWeight="400" align="center" color="#5B6167">
                                {step === 'password'
                                    ? `For your security, re-enter your password to continue to ${toolName}.`
                                    : `Add a business email to this account to continue to ${toolName}.`}
                            </Box>

                            <Box mb={2} mt={4} fontSize="14px" fontWeight="400" color="#292C2F">
                                <Box
                                    component="form"
                                    autoComplete="off"
                                    onSubmit={(e) => {
                                        e.preventDefault()
                                        if (step === 'password') submitPassword()
                                        else submitEmail()
                                    }}
                                >
                                    <Box mb={1} fontSize={13} color="#5B6167">Mobile</Box>
                                    <Box fontSize={16} fontWeight={600} color="primary.business" mb={2}>
                                        {`+63 ${mobileNum || ''}`}
                                    </Box>

                                    {step === 'password' ? (
                                        <Box>
                                            <Box display="flex">
                                                Password
                                                <Box color="red" ml={0.5} mb={1}>*</Box>
                                            </Box>
                                            <Box fontSize={13} color="#5B6167" mb={1}>This extra step helps protect your account.</Box>
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                type={showPass ? 'text' : 'password'}
                                                value={password}
                                                disabled={busy}
                                                error={Boolean(error)}
                                                fullWidth
                                                className={`${classes.tf} ${classes.border}`}
                                                onChange={(e) => setPassword(e.target.value)}
                                                inputProps={{ autoComplete: 'new-password' }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton onClick={() => setShowPass((v) => !v)}>
                                                                {showPass ? <IRemoveRedEye /> : <IVisibilityOff />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box display="flex">
                                                Business email
                                                <Box color="red" ml={0.5} mb={1}>*</Box>
                                            </Box>
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                type="text"
                                                value={bizEmail}
                                                disabled={busy}
                                                error={Boolean(error)}
                                                fullWidth
                                                className={`${classes.tf} ${classes.border}`}
                                                onChange={(e) => setBizEmail(e.target.value)}
                                                inputProps={{ autoComplete: 'off' }}
                                            />
                                        </Box>
                                    )}

                                    <Box color="error.main" fontSize={13} mt={0.5}>{error}</Box>

                                    <Button
                                        type="submit"
                                        size="large"
                                        variant="contained"
                                        className={classes.button}
                                        fullWidth
                                        disabled={busy}
                                        sx={{ marginBottom: '24px', marginTop: '16px' }}
                                    >
                                        {busy ? (
                                            <Box display="flex" alignItems="center" p={0.9}>
                                                <SnakeLoader bg="#ffffff" size="0.75rem" distance="0.3rem" />
                                            </Box>
                                        ) : (
                                            'Continue'
                                        )}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <Box display="flex" alignItems="center">
                    <img src={GSPFSS} alt="POFSIS" width="14" />
                    <Box fontSize={16} fontWeight="600" color="#333333" ml={1}>POFSIS</Box>
                </Box>
                <Box color="#5B6167" fontSize="12px" mb={3}>One account for Personal , Business and more.</Box>
            </Box>
        </Box>
    )
}
